import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Grid, CircularProgress, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { SaveRounded, ClearRounded } from "@mui/icons-material";
import { READ_UNIT_LIST, UPDATE_UNIT_LIST } from '../../../services/apiRoutes';
import CustomButton from '../../common/CustomButton';
import CustomTextField from '../../common/CustomTextField';
import CustomStyle from '../../common/CustomStyle';
import CustomAutocomplete from '../../common/CustomAutoComplete';
import { t } from '../../../constants/translationHelper';

const ValidationSchema = Yup.object().shape({
    unitName: Yup.string().required('Short Name is required'),
    unitType: Yup.string().required('unit Type is required'),
    formalName: Yup.string().required('Formal Name is required'),
    unitQualityCode: Yup.number().required('Unit Quality Code is required'),
    noOfDecimalPlaces: Yup.number().min(0, 'Cannot be negative').required('No. of Decimal Places is required'),
});

const UpdateUnitMaster = ({ handleClose, productsUnitId, unitTypeOptions, unitQuality, setMessage, fetchData, }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!productsUnitId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_UNIT_LIST(productsUnitId), {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                setData(response.data.unitMaster);

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [productsUnitId]);

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            const response = await axios.put(UPDATE_UNIT_LIST(productsUnitId), values, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: response.data.message || "updated successfully",
                    type: "success",
                });
                setTimeout(() => {
                    fetchData();
                    handleClose();
                },);
            } else {
                setMessage({
                    open: true,
                    text: 'Something went wrong',
                    type: 'error',
                });
            }
        } catch (error) {
            setMessage({
                open: true,
                text: 'Something went wrong',
                type: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik
                initialValues={{
                    unitName: data.unitName || '',
                    unitType: parseInt(data.unitType || ''),
                    formalName: data.formalName || '',
                    unitQualityCode: parseInt(data.unitQualityCode || ''),
                    noOfDecimalPlaces: data.noOfDecimalPlaces || '',
                }}
                validationSchema={ValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, errors, touched, handleChange, values, setFieldValue }) => (
                    <Form>
                        <Grid container spacing={1} style={CustomStyle.container}>
                            <Grid item xs={4} md={4}>
                                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                    {t("Update Unit Master")}
                                </Typography>
                            </Grid>
                            <Grid item xs={8} md={8} style={CustomStyle.buttonContainer}>
                                <Box style={CustomStyle.buttonBox}>
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                        size="small"
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting}
                                    />
                                    <CustomButton
                                        startIcon={<ClearRounded />}
                                        title="Cancel"
                                        size="small"
                                        variant="contained"
                                        onClick={handleClose}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid container spacing={2} sx={{ px: "0%" }}>
                            <Grid item xs={12} md={4}>
                                <CustomAutocomplete
                                    options={unitTypeOptions}
                                    fieldLabel="Unit Type"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={unitTypeOptions.find((option) => option.id === values.unitType) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('unitType', newValue ? newValue.id : '')
                                    }
                                    placeholder="Enter Unit Type"
                                    error={touched.unitType && !!errors.unitType}
                                    helperText={touched.unitType && errors.unitType}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CustomTextField
                                    placeholder="Enter Short Form"
                                    name="unitName"
                                    fieldLabel="Short Form of Unit"
                                    required
                                    value={values.unitName}
                                    onChange={handleChange}
                                    error={touched.unitName && !!errors.unitName}
                                    helperText={touched.unitName && errors.unitName}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CustomTextField
                                    placeholder="Enter Formal Name"
                                    name="formalName"
                                    fieldLabel="Formal Name"
                                    required
                                    value={values.formalName}
                                    onChange={handleChange}
                                    error={touched.formalName && !!errors.formalName}
                                    helperText={touched.formalName && errors.formalName}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CustomAutocomplete
                                    options={unitQuality}
                                    fieldLabel="Unit Quality Code (UQC)"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={unitQuality.find((option) => option.id === values.unitQualityCode) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('unitQualityCode', newValue ? newValue.id : '')
                                    }
                                    placeholder="Unit Quality Code"
                                    error={touched.unitQualityCode && !!errors.unitQualityCode}
                                    helperText={touched.unitQualityCode && errors.unitQualityCode}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CustomTextField
                                    placeholder="Enter Decimal Places"
                                    name="noOfDecimalPlaces"
                                    fieldLabel="No. Of Decimal Places"
                                    required
                                    type="number"
                                    value={values.noOfDecimalPlaces}
                                    onChange={handleChange}
                                    error={touched.noOfDecimalPlaces && !!errors.noOfDecimalPlaces}
                                    helperText={touched.noOfDecimalPlaces && errors.noOfDecimalPlaces}
                                />
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default UpdateUnitMaster;
